/**
 * T-6 · Le rapport d'avancement — le seul canal du tuteur vers la famille.
 *
 * Décision de conception reprise ici (§14.5) : il n'existe PAS de rapport
 * tuteur distinct. Un deuxième format doublerait le coût éditorial et créerait
 * deux vérités sur le même enfant. Il y a un seul document — celui que la
 * famille reçoit — et le tuteur l'annote avant qu'il ne parte.
 *
 * CE QUI EST BRANCHÉ : le document lui-même. Le plan de référence de l'élève
 * (`GET /v1/enfants/:id/plan-actif`) porte le `rapportId` du rapport dont il est
 * issu ; `GET /v1/rapports/:id` en rend le contenu — résumé, forces, lacunes,
 * pistes — et il est affiché tel quel, à la mesure de lecture d'un texte, jamais
 * reformulé — LOT 1.5 : ce document est COMPOSÉ PAR LE SERVEUR
 * (`modules/rapport-plan/document-famille.ts`), et c'est le même que celui que
 * le mobile du parent affiche. Les quatre contrôles éditoriaux voyagent avec
 * lui, exécutés sur lui.
 *
 * CE QUI N'EST PAS BRANCHÉ, ET DÉCLARÉ : le « mot du tuteur » (aucune route ne
 * persiste une annotation de tuteur sur un rapport), l'export PDF (B14, non
 * construit), la date d'envoi et le destinataire (aucun champ d'envoi au
 * contrat), et la comparaison avec le rapport précédent (rien n'expose la liste
 * des rapports d'un élève). Aucun de ces manques n'est simulé.
 */
import { useMemo } from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';

import { api } from '../api/client';
import { EmptyState } from '../components/EmptyState';
import { Chargement, ErreurRessource, NonBranche } from '../components/EtatRessource';
import { adressesManuelEleve } from '../lib/manuel-tuteur-format';
import { manuelVivantActif } from '../lib/manuel-vivant';
import { matiereLabel } from '../lib/suivi-format';
import { formatDate, libelleNiveauScolaire, pluriel } from '../lib/tuteur-format';
import { useAsync } from '../lib/useAsync';
import { useTuteur } from '../tuteur/TuteurContext';

/** Index — de quel élève veut-on relire le rapport ? */
export function RapportsPage() {
  const { eleves } = useTuteur();
  return (
    <div className="content__inner">
      <div className="page-head">
        <div>
          <h1>Rapports</h1>
          <p className="page-sub">Un seul document par élève, celui que la famille reçoit.</p>
        </div>
      </div>

      {eleves.status === 'loading' ? <Chargement quoi="votre périmètre" /> : null}
      {eleves.status === 'error' ? <ErreurRessource message={eleves.message} /> : null}
      {eleves.status === 'ready' && eleves.data.nbEleves === 0 ? (
        <EmptyState title="Aucun élève ne vous est affecté">
          Un rapport porte sur un élève suivi.
        </EmptyState>
      ) : null}
      {eleves.status === 'ready' && eleves.data.nbEleves > 0 ? (
        <div className="list">
          {eleves.data.eleves.map((e) => (
            <Link key={e.eleveId} className="li li--link" to={`/rapports/${e.eleveId}`}>
              <span className="li-main">
                <span className="li-title">{e.prenom}</span>
                <span className="li-sub">
                  {libelleNiveauScolaire(e.niveauScolaire)} ·{' '}
                  {e.matieresActives.map(matiereLabel).join(', ') || 'aucune matière active'}
                </span>
              </span>
              <span className="btn btn--ghost btn--sm">Ouvrir son rapport</span>
            </Link>
          ))}
        </div>
      ) : null}
    </div>
  );
}

export function RapportPage() {
  const { eleveId } = useParams<'eleveId'>();
  const { eleves } = useTuteur();

  /* Chantier « manuel vivant », Φ-MV-5 (MV-TUT-11, MV-DEF-25) — capacité
     allumée, il n'y a qu'UN rapport : le dernier que la famille a reçu, sur sa
     page (`RapportUniquePage`). Celle-ci montrait le rapport du plan actif, qui
     n'est pas forcément le même : elle y renvoie, sans rien demander au
     serveur. Capacité éteinte, rien ne change. */
  const unique = manuelVivantActif();
  const plan = useAsync(
    useMemo(() => (eleveId && !unique ? () => api.getPlanActif(eleveId) : null), [eleveId, unique]),
  );

  const rapportId = plan.status === 'ready' ? (plan.data.plan?.rapportId ?? null) : null;
  const rapport = useAsync(
    useMemo(() => (rapportId ? () => api.getRapport(rapportId) : null), [rapportId]),
  );

  const eleve =
    eleves.status === 'ready' ? eleves.data.eleves.find((e) => e.eleveId === eleveId) : undefined;

  // LOT 1.5 — l'écran ne projette plus rien. Le document vient composé du
  // serveur, avec ses contrôles exécutés SUR LUI : « Ce que la famille lira,
  // exactement » est enfin exact.
  const document = rapport.status === 'ready' ? rapport.data.document : null;

  if (unique && eleveId) return <Navigate to={adressesManuelEleve.rapport(eleveId)} replace />;

  return (
    <div className="content__inner">
      <div className="dash-topbar">
        <div>
          <p className="breadcrumb">
            <Link to="/ma-journee">Ma journée</Link> / <Link to="/rapports">Rapports</Link> /{' '}
            {eleve?.prenom ?? 'élève'}
          </p>
          <h1>Rapport de {eleve?.prenom ?? 'cet élève'}</h1>
          {rapport.status === 'ready' ? (
            <p className="page-sub">
              Établi le {formatDate(rapport.data.genereLe)} · destinataire : le parent titulaire du
              compte
            </p>
          ) : null}
        </div>
        {eleveId ? (
          <Link className="btn btn--ghost btn--sm" to={`/eleves/${eleveId}`}>
            Sa fiche
          </Link>
        ) : null}
      </div>

      {plan.status === 'loading' ? <Chargement quoi="le plan de référence" /> : null}
      {plan.status === 'error' ? <ErreurRessource message={plan.message} /> : null}
      {plan.status === 'ready' && !plan.data.plan ? (
        <EmptyState title="Aucun rapport pour cet élève">
          Le rapport naît du bilan de départ, et c'est le plan de référence approuvé qui le
          référence. Tant qu'aucun plan n'existe, il n'y a aucun document à relire — et rien n'est
          affiché à la place.
        </EmptyState>
      ) : null}
      {rapport.status === 'loading' && rapportId ? <Chargement quoi="le document" /> : null}
      {rapport.status === 'error' ? <ErreurRessource message={rapport.message} /> : null}

      {document ? (
        <div className="tuteur-grid">
          <div className="stack">
            <section className="card rapport-carte">
              <span className="stage-label">Ce que la famille lira, exactement</span>
              {/* LOT 1.5 — cet écran composait sa PROPRE mise en page du même
                  contenu : le résumé entier en tête là où la famille ne voit
                  que sa première phrase, TOUTES les pistes là où elle n'en voit
                  qu'une, et une mention légale à laquelle il ajoutait une
                  phrase de son cru. L'étiquette promettait « exactement » et
                  montrait autre chose. Le document vient désormais composé du
                  serveur, et les deux surfaces rendent le même. */}
              <div className="rapport-doc">
                <p className="phrase-parent">{document.phrase}</p>
                {document.contexte ? <p>{document.contexte}</p> : null}

                {document.solide.length > 0 ? (
                  <>
                    <h4>{document.titreSolide}</h4>
                    {document.solide.map((s) => (
                      <p key={s.savoirId}>{s.enonce}</p>
                    ))}
                  </>
                ) : null}

                {document.travaille.length > 0 ? (
                  <>
                    <h4>{document.titreTravaille}</h4>
                    {document.travaille.map((s) => (
                      <p key={s.savoirId}>{s.enonce}</p>
                    ))}
                  </>
                ) : null}

                {document.prochaineEtape ? (
                  <>
                    <h4>{document.titreProchaineEtape}</h4>
                    <p>{document.prochaineEtape}</p>
                  </>
                ) : null}

                {document.reperesQuebec.length > 0 ? (
                  <>
                    <h4>Ce que ça veut dire au Québec</h4>
                    {document.reperesQuebec.map((r) => (
                      <p key={r}>{r}</p>
                    ))}
                  </>
                ) : null}

                <hr className="sep-soft" />
                <p className="mention-legale">{document.mentionLegale}</p>
              </div>
            </section>

            <NonBranche titre="Exporter en PDF">
              L'export PDF porte trois usages — transmission, second parent, école — et remplace à
              lui seul une plateforme de partage. Il n'est pas construit : aucune route ne rend ce
              document en PDF. Le déclarer vaut mieux qu'un bouton qui ne fait rien.
            </NonBranche>
          </div>

          <div className="stack">
            <section className="card">
              <h3>Votre apport — le mot du professeur</h3>
              <p className="card-sub">
                La seule partie du document que vous rédigez : ce que vous avez vu en séance et que
                les données ne savent pas.
              </p>
              <label className="field field--bloc">
                <span className="lbl">Le mot du tuteur</span>
                <textarea
                  className="control"
                  rows={5}
                  disabled
                  placeholder="Champ inactif — rien ne l'enregistre encore."
                />
                <span className="hint">
                  Ce champ est inactif : rien ne persiste encore une annotation de tuteur sur un
                  rapport.
                </span>
              </label>
            </section>

            <section className="card">
              <h3>Avant d'envoyer</h3>
              <p className="card-sub">
                Contrôles exécutés par le serveur sur le document ci-contre, celui qui part.
              </p>
              <div className="stack-sm controles">
                {document.controles.map((c) => (
                  <div key={c.id} className={c.passe ? 'controle is-ok' : 'controle is-ko'}>
                    <span className="controle__marque" aria-hidden="true" />
                    <span>
                      <span className="sr-only">
                        {c.passe ? 'Contrôle réussi : ' : 'Contrôle en échec : '}
                      </span>
                      {c.libelle}
                      {!c.passe && c.extrait ? (
                        <span className="controle__extrait">« {c.extrait} »</span>
                      ) : null}
                    </span>
                  </div>
                ))}
              </div>
              <p className="note-line">
                Ils ne remplacent pas votre relecture : ils attrapent les quatre fautes qui violent
                un invariant du produit.
              </p>
            </section>

            <section className="card card--calm">
              <h3>Ce que vous ne pouvez pas modifier</h3>
              <p className="lede">
                Constats produits à partir des données. Si l'un est faux, corrigez la donnée à la
                source, pas le texte.
              </p>
              {/* LOT 3.5 (CON-TUT-RAP-07) — « LE LIEN EST APPROXIMATIF.
                  "Corriger un palier de maîtrise" est un `<a href="/validation">`
                  : il ouvre la file de validation GÉNÉRALE, sans filtre sur
                  l'élève du rapport, et sans le savoir visé. Ce n'est
                  "l'endroit où l'on corrige un palier" que si cet élève a
                  précisément une évaluation en attente. »

                  Le lien porte désormais l'élève. La file de validation le
                  reçoit déjà en paramètre (`valider.tsx` positionne son index
                  sur l'évaluation nommée) : ce qui manquait n'était pas une
                  capacité, c'était le paramètre. Et quand la page n'a pas
                  d'élève en contexte, on ne fabrique pas un lien qui pointe
                  ailleurs — on garde la file générale. */}
              <Link
                className="btn btn--ghost btn--sm btn--block"
                to={eleveId ? `/validation?eleveId=${encodeURIComponent(eleveId)}` : '/validation'}
              >
                Corriger un palier de maîtrise
              </Link>
              <p className="note-line">
                {pluriel(document.solide.length, 'savoir cité en force', 'savoirs cités en force')}{' '}
                ·{' '}
                {pluriel(document.travaille.length, 'savoir à travailler', 'savoirs à travailler')}.
              </p>
            </section>

            <NonBranche titre="Envoi">
              Le contrat ne porte ni destinataire, ni date d'envoi, ni historique des rapports d'un
              élève : ni « départ automatique vendredi », ni « retarder d'une semaine », ni
              comparaison avec le rapport précédent ne peuvent être affichés honnêtement.
            </NonBranche>
          </div>
        </div>
      ) : null}
    </div>
  );
}
